// The calendars screen's pure rules (spec 03 §4.8): the segment checks the
// screen runs before a save, a pattern's one-line summary, the exception form's
// checks, facility wall time to an instant, and the small labels. The checks
// mirror the calendar kernel's (§3.3a) so the common mistake never makes the
// trip; the server stays the judge, and its 422 lands on the same rows.
//
// The web app may not import `packages/scheduler` (its import boundary admits
// module 03's server alone, `assessly/scheduler-boundary`), so the rules below
// are written again from §3.3a rather than shared.

import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { facilityDayStartUtcMs, facilityTodayIso } from "../../../../lib/business-dates";

// ---- patterns ------------------------------------------------------------------

/** A segment as the editor holds it: every box is text until it is sent. */
export interface SegmentDraft {
  readonly weekday: string;
  readonly start: string;
  readonly end: string;
  readonly shift_code: string;
  readonly efficiency_pct: string;
}

export interface WireSegment {
  readonly weekday: number;
  readonly start: string;
  readonly end: string;
  readonly shift_code: string;
  readonly efficiency_pct: number;
}

/**
 * The reasons a segment is refused. The first eight are the kernel's
 * (`SHIFT_PATTERN_INVALID`, §3.3a); `shift_code_too_long` is the 400 of §3.3b
 * and `too_many_segments` the 42-segment cap, both judged here first.
 */
export type SegmentReason =
  | "no_segments"
  | "weekday_out_of_range"
  | "start_malformed"
  | "end_malformed"
  | "shift_code_missing"
  | "efficiency_out_of_range"
  | "zero_length"
  | "overlaps"
  | "shift_code_too_long"
  | "too_many_segments";

export interface SegmentIssue {
  /** The failing segment, or null for the pattern as a whole. */
  readonly index: number | null;
  readonly reason: SegmentReason;
  /** For `overlaps`, the segment it overlaps. */
  readonly other_index?: number | undefined;
}

const WALL_TIME = /^([01]\d|2[0-3]):[0-5]\d$/u;
const MINUTES_PER_DAY = 1440;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;
export const SHIFT_CODE_MAX = 32;
export const SEGMENTS_MAX = 42;

/** `HH:MM` as minutes after midnight, or undefined when it is not a wall time. */
export function wallMinutes(text: string): number | undefined {
  const raw = text.trim();
  if (!WALL_TIME.test(raw)) return undefined;
  const [h, m] = raw.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** True when a segment runs past midnight into the next day (`end` before `start`). */
export function crossesMidnight(draft: Pick<SegmentDraft, "start" | "end">): boolean {
  const start = wallMinutes(draft.start);
  const end = wallMinutes(draft.end);
  return start !== undefined && end !== undefined && end < start;
}

function wholeIn(text: string, min: number, max: number): number | undefined {
  const raw = text.trim();
  if (!/^\d+$/u.test(raw)) return undefined;
  const n = Number(raw);
  return n >= min && n <= max ? n : undefined;
}

/**
 * Every issue the editor's segments carry, ordered by segment then reason, as
 * the kernel lists them. Overlap is judged on the week as a circle of wall
 * minutes, so Sunday's night shift meets Monday's morning, and each segment of
 * an overlapping pair carries its own entry naming the other.
 */
export function checkSegments(drafts: readonly SegmentDraft[]): SegmentIssue[] {
  if (drafts.length === 0) return [{ index: null, reason: "no_segments" }];
  const issues: SegmentIssue[] = [];
  if (drafts.length > SEGMENTS_MAX) issues.push({ index: null, reason: "too_many_segments" });
  const spans: ({ start: number; length: number } | undefined)[] = [];
  drafts.forEach((d, index) => {
    const weekday = wholeIn(d.weekday, 1, 7);
    const start = wallMinutes(d.start);
    const end = wallMinutes(d.end);
    if (weekday === undefined) issues.push({ index, reason: "weekday_out_of_range" });
    if (start === undefined) issues.push({ index, reason: "start_malformed" });
    if (end === undefined) issues.push({ index, reason: "end_malformed" });
    const code = d.shift_code.trim();
    if (code.length === 0) issues.push({ index, reason: "shift_code_missing" });
    else if (code.length > SHIFT_CODE_MAX) issues.push({ index, reason: "shift_code_too_long" });
    if (wholeIn(d.efficiency_pct, 10, 200) === undefined)
      issues.push({ index, reason: "efficiency_out_of_range" });
    if (start !== undefined && end !== undefined && start === end)
      issues.push({ index, reason: "zero_length" });
    spans.push(
      weekday !== undefined && start !== undefined && end !== undefined && start !== end
        ? {
            start: (weekday - 1) * MINUTES_PER_DAY + start,
            length: end > start ? end - start : end + MINUTES_PER_DAY - start,
          }
        : undefined,
    );
  });
  const overlaps: SegmentIssue[] = [];
  for (let i = 0; i < spans.length; i += 1) {
    for (let j = i + 1; j < spans.length; j += 1) {
      const a = spans[i];
      const b = spans[j];
      if (a === undefined || b === undefined) continue;
      if (spansMeet(a, b)) {
        overlaps.push({ index: i, reason: "overlaps", other_index: j });
        overlaps.push({ index: j, reason: "overlaps", other_index: i });
      }
    }
  }
  return [...issues, ...overlaps].sort(
    (x, y) => (x.index ?? -1) - (y.index ?? -1) || (x.other_index ?? -1) - (y.other_index ?? -1),
  );
}

/** Two arcs of the week circle share a minute. */
function spansMeet(
  a: { start: number; length: number },
  b: { start: number; length: number },
): boolean {
  const ab = (((b.start - a.start) % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;
  const ba = (((a.start - b.start) % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;
  return ab < a.length || ba < b.length;
}

/** The segments as the API takes them, or undefined while any box is not valid. */
export function segmentsToWire(drafts: readonly SegmentDraft[]): WireSegment[] | undefined {
  if (checkSegments(drafts).length > 0) return undefined;
  return drafts.map((d) => ({
    weekday: Number(d.weekday.trim()),
    start: d.start.trim(),
    end: d.end.trim(),
    shift_code: d.shift_code.trim(),
    efficiency_pct: Number(d.efficiency_pct.trim()),
  }));
}

/** A stored segment back into the editor's boxes. */
export function segmentToDraft(s: WireSegment): SegmentDraft {
  return {
    weekday: String(s.weekday),
    start: s.start,
    end: s.end,
    shift_code: s.shift_code,
    efficiency_pct: String(s.efficiency_pct),
  };
}

/**
 * The quick add: one segment per ticked weekday with the same times, code and
 * efficiency, appended in weekday order. A day already holding an identical
 * segment is skipped, so pressing Add twice adds nothing twice.
 */
export function addSegmentsForDays(
  drafts: readonly SegmentDraft[],
  template: Omit<SegmentDraft, "weekday">,
  weekdays: readonly number[],
): SegmentDraft[] {
  const out = [...drafts];
  const same = (d: SegmentDraft, day: number) =>
    d.weekday.trim() === String(day) &&
    d.start.trim() === template.start.trim() &&
    d.end.trim() === template.end.trim() &&
    d.shift_code.trim() === template.shift_code.trim();
  for (const day of [...new Set(weekdays)].sort((a, b) => a - b)) {
    if (out.some((d) => same(d, day))) continue;
    out.push({ ...template, weekday: String(day) });
  }
  return out;
}

/** The weekday's short name in the reader's locale, Monday 1 to Sunday 7. */
export function weekdayName(weekday: number, locale: string, width: "short" | "long" = "short") {
  // 2024-01-01 was a Monday.
  const stamp = Date.UTC(2024, 0, weekday);
  try {
    return new Intl.DateTimeFormat(locale, { weekday: width, timeZone: "UTC" }).format(stamp);
  } catch {
    return String(weekday);
  }
}

export interface SegmentGroup {
  /** ISO weekdays in order. */
  readonly weekdays: readonly number[];
  readonly start: string;
  readonly end: string;
  readonly shift_code: string;
  readonly efficiency_pct: number;
}

/**
 * A pattern's segments grouped by everything but the weekday, in the order the
 * first of each group appears: the ten segments of a two-shift week read as two
 * lines, "Mon–Fri 08:00–20:00 DAY 100 %" and "Mon–Fri 20:00–08:00 NIGHT 70 %".
 */
export function groupSegments(segments: readonly WireSegment[]): SegmentGroup[] {
  const groups: { key: string; group: SegmentGroup & { weekdays: number[] } }[] = [];
  for (const s of segments) {
    const key = `${s.start}|${s.end}|${s.shift_code}|${String(s.efficiency_pct)}`;
    const found = groups.find((g) => g.key === key);
    if (found === undefined) {
      groups.push({
        key,
        group: {
          weekdays: [s.weekday],
          start: s.start,
          end: s.end,
          shift_code: s.shift_code,
          efficiency_pct: s.efficiency_pct,
        },
      });
    } else if (!found.group.weekdays.includes(s.weekday)) {
      found.group.weekdays.push(s.weekday);
    }
  }
  return groups.map(({ group }) => ({
    ...group,
    weekdays: [...group.weekdays].sort((a, b) => a - b),
  }));
}

/** Weekdays as runs of names: [1,2,3,4,5,7] reads "Mon–Fri, Sun". */
export function weekdayRuns(weekdays: readonly number[], locale: string): string {
  const days = [...new Set(weekdays)].sort((a, b) => a - b);
  const runs: string[] = [];
  let i = 0;
  while (i < days.length) {
    let j = i;
    while (j + 1 < days.length && days[j + 1] === (days[j] ?? 0) + 1) j += 1;
    const first = days[i] ?? 1;
    const last = days[j] ?? 1;
    runs.push(
      j - i >= 2
        ? `${weekdayName(first, locale)}–${weekdayName(last, locale)}`
        : days
            .slice(i, j + 1)
            .map((d) => weekdayName(d, locale))
            .join(", "),
    );
    i = j + 1;
  }
  return runs.join(", ");
}

// ---- exceptions ----------------------------------------------------------------

export type ExceptionKind =
  "holiday" | "planned_maintenance" | "breakdown" | "extra_shift" | "overtime";

/** The kinds that may raise capacity above 1 (§2.14). */
export const BOOSTING_KINDS: ReadonlySet<ExceptionKind> = new Set(["extra_shift", "overtime"]);

/** The factor the form offers for a kind until the reader types one. */
export function defaultFactor(kind: ExceptionKind): string {
  if (kind === "extra_shift") return "1";
  if (kind === "overtime") return "1.5";
  return "0";
}

/**
 * A capacity factor box as the API's decimal string (at most two places, a
 * comma read as the point), or undefined when it is not one.
 */
export function parseFactor(text: string): string | undefined {
  const raw = text.trim().replace(",", ".");
  if (!/^\d{1,3}(\.\d{1,2})?$/u.test(raw)) return undefined;
  return raw;
}

export type ExceptionReason =
  | "starts_malformed"
  | "ends_malformed"
  | "ends_not_after_starts"
  | "factor_malformed"
  | "factor_out_of_range"
  | "factor_above_one_for_kind"
  | "note_too_long";

export interface ExceptionDraft {
  readonly kind: ExceptionKind;
  /** True when the exception covers whole facility days. */
  readonly allDay: boolean;
  readonly startDate: string;
  readonly startTime: string;
  readonly endDate: string;
  readonly endTime: string;
  readonly factor: string;
  readonly note: string;
}

export const EXCEPTION_NOTE_MAX = 500;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;

/**
 * The form's two instants in the facility's zone. An all-day exception runs
 * from the local start of its first date to the local start of the day after
 * its last, so "25 Dec to 25 Dec" closes exactly that facility day.
 */
export function exceptionInstants(
  draft: ExceptionDraft,
  timezone: string | null | undefined,
): { startsAt: number | undefined; endsAt: number | undefined } {
  const startsAt = !ISO_DATE.test(draft.startDate)
    ? undefined
    : draft.allDay
      ? facilityDayStartUtcMs(draft.startDate, timezone)
      : wallMinutes(draft.startTime) === undefined
        ? undefined
        : wallToUtcMs(draft.startDate, draft.startTime, timezone);
  const endsAt = !ISO_DATE.test(draft.endDate)
    ? undefined
    : draft.allDay
      ? facilityDayStartUtcMs(addDaysIso(draft.endDate, 1), timezone)
      : wallMinutes(draft.endTime) === undefined
        ? undefined
        : wallToUtcMs(draft.endDate, draft.endTime, timezone);
  return { startsAt, endsAt };
}

/** Every reason the exception form is refused before it is sent (§2.14, §3.3a, §3.3b). */
export function checkException(
  draft: ExceptionDraft,
  timezone: string | null | undefined,
): ExceptionReason[] {
  const reasons: ExceptionReason[] = [];
  const { startsAt, endsAt } = exceptionInstants(draft, timezone);
  if (startsAt === undefined) reasons.push("starts_malformed");
  if (endsAt === undefined) reasons.push("ends_malformed");
  if (startsAt !== undefined && endsAt !== undefined && endsAt <= startsAt)
    reasons.push("ends_not_after_starts");
  const factor = parseFactor(draft.factor);
  if (factor === undefined) reasons.push("factor_malformed");
  else if (Number(factor) > 3) reasons.push("factor_out_of_range");
  else if (Number(factor) > 1 && !BOOSTING_KINDS.has(draft.kind))
    reasons.push("factor_above_one_for_kind");
  if (draft.note.trim().length > EXCEPTION_NOTE_MAX) reasons.push("note_too_long");
  return reasons;
}

// ---- facility wall time --------------------------------------------------------

const wallFormatters = new Map<string, Intl.DateTimeFormat | null>();

/** The instant's wall clock in the zone as `YYYY-MM-DDTHH:MM`, comparable as text. */
export function zoneWall(ms: number, timezone: string): string | undefined {
  let fmt = wallFormatters.get(timezone);
  if (fmt === undefined) {
    try {
      fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
    } catch {
      fmt = null;
    }
    wallFormatters.set(timezone, fmt);
  }
  if (fmt === null) return undefined;
  const parts = fmt.formatToParts(ms);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const hour = part("hour") === "24" ? "00" : part("hour");
  return `${part("year")}-${part("month")}-${part("day")}T${hour}:${part("minute")}`;
}

/**
 * A facility-local date and wall time as a UTC instant. A wall time the zone
 * reads twice (a fall-back night) resolves to its first occurrence, and one it
 * skips (a spring-forward gap) to the instant the gap ends, the kernel's rules
 * (§3.3a), so the screen and the server read a typed time alike. Without a
 * zone, or with one the runtime does not know, the browser's clock is read.
 */
export function wallToUtcMs(
  dateIso: string,
  hhmm: string,
  timezone: string | null | undefined,
): number {
  const [y, mo, d] = dateIso.split("-").map(Number);
  const minutes = wallMinutes(hhmm) ?? 0;
  const naive = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1, Math.floor(minutes / 60), minutes % 60);
  const target = `${dateIso}T${hhmm.trim()}`;
  if (
    typeof timezone !== "string" ||
    timezone.length === 0 ||
    zoneWall(0, timezone) === undefined
  ) {
    return new Date(
      y ?? 1970,
      (mo ?? 1) - 1,
      d ?? 1,
      Math.floor(minutes / 60),
      minutes % 60,
    ).getTime();
  }
  const offsetAt = (ms: number): number => {
    const wall = zoneWall(ms, timezone) ?? "";
    const [date, time] = wall.split("T");
    const [wy, wm, wd] = (date ?? "").split("-").map(Number);
    const [wh, wmin] = (time ?? "").split(":").map(Number);
    const asUtc = Date.UTC(wy ?? 1970, (wm ?? 1) - 1, wd ?? 1, wh ?? 0, wmin ?? 0);
    return asUtc - Math.floor(ms / 60_000) * 60_000;
  };
  const day = 86_400_000;
  const offsets = [...new Set([offsetAt(naive - day), offsetAt(naive), offsetAt(naive + day)])];
  const candidates = offsets.map((o) => naive - o).sort((a, b) => a - b);
  const matches = candidates.filter((c) => zoneWall(c, timezone) === target);
  if (matches.length > 0) return matches[0] ?? naive;
  // A spring-forward gap: the first instant whose wall clock reads past the target.
  let lo = (candidates[0] ?? naive) - day;
  let hi = (candidates[candidates.length - 1] ?? naive) + day;
  while ((zoneWall(lo, timezone) ?? "") >= target) lo -= day;
  while ((zoneWall(hi, timezone) ?? "") < target) hi += day;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if ((zoneWall(mid, timezone) ?? "") < target) lo = mid;
    else hi = mid;
  }
  return hi;
}

// ---- dates and labels ----------------------------------------------------------

/** Today at the facility. */
export function facilityToday(timezone: string | null | undefined, nowMs = Date.now()): string {
  return facilityTodayIso(nowMs, timezone);
}

/** Inclusive days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export type WindowProblem = "malformed" | "reversed" | "too_wide";

/** Why an exceptions window cannot be read, or undefined when it can (row 33). */
export function checkWindow(from: string, to: string, maxDays: number): WindowProblem | undefined {
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to)) return "malformed";
  const span = daysBetween(from, to);
  if (Number.isNaN(span)) return "malformed";
  if (span < 0) return "reversed";
  if (span + 1 > maxDays) return "too_wide";
  return undefined;
}

/** True when the assignment starts after the facility's today, the one case route 32 removes. */
export function isFutureAssignment(validFrom: string, today: string): boolean {
  return validFrom > today;
}

/** True when the assignment covers the facility's today. */
export function isCurrentAssignment(
  validFrom: string,
  validTo: string | null,
  today: string,
): boolean {
  return validFrom <= today && (validTo === null || validTo >= today);
}

/** A work centre's name in the reader's language, else English, else any, else its code. */
export function workCenterLabel(
  wc: { code: string; name_i18n: Readonly<Record<string, string | undefined>> },
  locale: string,
): string {
  const lang = locale.slice(0, 2).toLowerCase();
  const bundle = wc.name_i18n;
  const pick = (key: string) => {
    const v = bundle[key];
    return v !== undefined && v.trim().length > 0 ? v : undefined;
  };
  const name =
    pick(lang) ??
    pick("en") ??
    Object.keys(bundle)
      .sort()
      .map(pick)
      .find((v) => v !== undefined);
  return name === undefined ? wc.code : `${wc.code} · ${name}`;
}

/**
 * The shift names module 04 gives each shift code, in the reader's language,
 * else English, else any. A code with no name in any language is left out, so
 * the caller can fall back to the code itself.
 */
export function shiftNamesByCode(
  definitions: readonly {
    code: string;
    name_i18n: Readonly<Record<string, string | undefined>>;
  }[],
  locale: string,
): Map<string, string> {
  const lang = locale.slice(0, 2).toLowerCase();
  const out = new Map<string, string>();
  for (const def of definitions) {
    if (out.has(def.code)) continue;
    const pick = (key: string) => {
      const v = def.name_i18n[key];
      return v !== undefined && v.trim().length > 0 ? v.trim() : undefined;
    };
    const name =
      pick(lang) ??
      pick("en") ??
      Object.keys(def.name_i18n)
        .sort()
        .map(pick)
        .find((v) => v !== undefined);
    if (name !== undefined) out.set(def.code, name);
  }
  return out;
}

const hourFormatters = new Map<string, Intl.NumberFormat>();

/** Minutes as hours in the reader's locale, at most two decimals: 690 reads "11.5". */
export function formatHours(minutes: number, locale: string): string {
  let fmt = hourFormatters.get(locale);
  if (fmt === undefined) {
    try {
      fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    } catch {
      fmt = new Intl.NumberFormat("en", { maximumFractionDigits: 2 });
    }
    hourFormatters.set(locale, fmt);
  }
  return fmt.format(Math.round((minutes / 60) * 100) / 100);
}

/** A capacity factor for reading: "0.00" is closed, anything else a multiplier. */
export function factorIsClosed(factor: string): boolean {
  return Number(factor) === 0;
}

/** A factor without trailing zeros in the reader's locale: "1.50" reads "1,5" in ru. */
export function formatFactor(factor: string, locale: string): string {
  const n = Number(factor);
  if (Number.isNaN(n)) return factor;
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
  } catch {
    return String(n);
  }
}
